import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Chip from "@mui/material/Chip";
import Grow from "@mui/material/Grow";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { styled } from "@mui/material/styles";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import _, { uniqBy, throttle } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useSession } from "users/__session/session-context";
import {
  getCommentsByProposal,
  getCommentsLoadError,
  getIsLoadingComments,
  populateThreadComments,
} from "work/proposals/comments/_store/comments-redux-slice";
import Comment from "work/proposals/comments/comment";

import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import CommentAudienceSwitch from "work/proposals/comments/_view/comment-audience-switch";
import CommentComposer from "work/proposals/comments/_view/comment-composer";
import CommentItem from "work/proposals/comments/_view/comment-item";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";
import { ProposalEmptyPaneMessage } from "work/proposals/_views/_shared/proposal-styled-components";
import { useSelector } from "react-redux";

const CommentsContainer = styled("div")(() => ({
  display: "contents",
  height: "100%",
}));
const CommentsList = styled(List)(() => ({
  overflowY: "auto",
}));
const BackToTopChipContainer = styled("div")(() => ({
  display: "flex",
  height: 0,
  justifyContent: "center",
  position: "relative",
  width: "100%",
  zIndex: 1,
}));
const BackToTopChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  top: "20px",
  boxShadow: "0 4px 5px 0 rgb(0 0 0 / 14%), 0 1px 10px 0 rgb(0 0 0 / 12%), 0 2px 4px 0 rgb(0 0 0 / 20%)",
  color: "white",
  position: "absolute",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
}));

type CommentsProps = {
  className?: string;
  proposal: Proposal;
  commentThreads: CommentThread[];
  isSaving?: boolean;
  readOnly?: boolean;
  showMarkAllRead?: boolean;
};

/**
 * The negotiation's conversation on its own — composer plus comment list.
 *
 * The live proposal pages show the conversation interleaved with the change history instead (see
 * `CommentsDrawer`), so this list is the archive's view: a finished negotiation whose changes are no
 * longer being made.
 */
export default function Comments(props: Readonly<CommentsProps>) {
  const { className, proposal, commentThreads } = props;
  const isSaving = props.isSaving ?? false;
  const readOnly = props.readOnly ?? false;
  const showMarkAllRead = props.showMarkAllRead ?? true;

  const topRef = useRef<HTMLDivElement>(null);
  const commentsListRef = useRef<HTMLUListElement>(null);

  const [backToTopVisible, setBackToTopVisible] = React.useState(false);

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const proposalComments = useSelector(getCommentsByProposal(proposal.id));
  const isLoadingComments = useSelector(getIsLoadingComments(proposal.id));
  const commentsLoadError = useSelector(getCommentsLoadError(proposal.id));

  const handleWindowScrolled = useCallback((event: Event) => {
    const el = event.currentTarget as HTMLElement | null;
    if (!el) return;

    const denom = el.scrollHeight - el.clientHeight;
    const scrollPercent = denom > 0 ? Math.round((el.scrollTop / denom) * 100) : 0;

    setBackToTopVisible(scrollPercent > 5);
  }, []);

  const throttledScrollEventHandler = useMemo(() => {
    return throttle(handleWindowScrolled, Config.backToTopThrottleDelay);
  }, [handleWindowScrolled]);

  const scrollToBottom = useCallback(() => {
    globalThis.setTimeout(() => {
      topRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }, 500);
  }, []);

  const subscriberUserIds = useMemo(
    () =>
      uniqBy(
        [
          ...(proposalComments?.map((comment: Comment) => comment.senderId).filter(Boolean) ?? []),
          ...commentThreads.flatMap((thread: CommentThread) => thread.subscribers.map((sub) => sub.userId)),
        ],
        (id) => id?.value,
      )
        .filter(Boolean)
        .map((id) => id!.value),
    [proposalComments, commentThreads],
  );
  const subscriberUserIdsSet = useMemo(() => new Set(subscriberUserIds), [subscriberUserIds]);

  useGetIndividualsByUserIdsQuery({ userIds: subscriberUserIdsSet }, { skip: subscriberUserIds.length === 0 });

  useEffect(() => {
    // Attach the listener when the element exists, and clean up properly
    const el = commentsListRef.current;
    if (!el) return;

    el.addEventListener("scroll", throttledScrollEventHandler);

    return () => {
      el.removeEventListener("scroll", throttledScrollEventHandler);
      throttledScrollEventHandler.cancel(); // lodash throttle cleanup
    };
  }, [throttledScrollEventHandler]);

  useEffect(() => {
    scrollToBottom();
  }, [commentThreads, proposalComments, scrollToBottom]);

  // Comment bodies are normally pre-loaded when the negotiation's threads enter the store
  // (populateCommentThreadsByProposal), so on open `proposalComments` is already defined and this effect
  // is a no-op. It remains a fallback for a negotiation whose threads are not yet in the store. It must
  // load exactly once and then settle: `proposalComments !== undefined` covers a successful (even empty)
  // load, and `commentsLoadError` covers a rejected one — without the error guard a rejected populate
  // (which clears `loading` but never writes `entries`) would satisfy the re-populate condition on
  // every render and churn the loader forever.
  useEffect(() => {
    if (isLoadingComments || proposalComments !== undefined || commentsLoadError) return;

    commentThreads.forEach((thread) => {
      dispatch(populateThreadComments({ session, thread }));
    });
  }, [proposalComments, commentThreads, dispatch, isLoadingComments, commentsLoadError, session]);

  // Newest first, by the moment each comment entered the conversation. A comment that has not entered
  // it yet — the one the author just posted, still on the wire — is the newest thing here rather than
  // the oldest, so it sorts above everything instead of falling to the foot of the list and jumping to
  // the top when the server answers (#5125). See `Comment.enteredConversationAt`.
  const sortedComments = _(proposalComments)
    .uniqBy((c) => c.id?.value)
    .orderBy((c) => c.enteredConversationAt?.valueOf() ?? Number.POSITIVE_INFINITY, "desc")
    .value();

  // Keep the composer permanently mounted so an in-flight mark-read/refetch (which flips
  // isLoadingComments) can never unmount the input mid-type and drop the user's comment. The loader
  // is scoped to the comment list only.
  return (
    <CommentsContainer className={className}>
      <CommentComposer
        proposal={proposal}
        commentThreads={commentThreads}
        isSaving={isSaving}
        readOnly={readOnly}
        showMarkAllRead={showMarkAllRead}
        onCommentPosted={scrollToBottom}
      />
      {isLoadingComments ? (
        <Loader />
      ) : (
        <>
          {Boolean(proposalComments?.length && proposalComments.length > 0) && (
            <BackToTopChipContainer>
              <Grow in={backToTopVisible}>
                <BackToTopChip
                  label="Jump to latest"
                  icon={<ArrowUpwardIcon style={{ color: "white" }} />}
                  onClick={() =>
                    topRef?.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                      inline: "nearest",
                    })
                  }
                />
              </Grow>
            </BackToTopChipContainer>
          )}
          <CommentsList ref={commentsListRef}>
            <div ref={topRef} />
            {sortedComments?.length === 0 && (
              <ListItem key={readOnly ? "NoComments" : Guid.generate().value} disableGutters>
                <ProposalEmptyPaneMessage>
                  {readOnly ? "No comments to display" : "No comments yet"}
                </ProposalEmptyPaneMessage>
              </ListItem>
            )}
            {sortedComments.length > 0 &&
              sortedComments?.map((comment) => (
                <CommentItem
                  key={comment.id?.value}
                  comment={comment}
                  renderAudienceSwitch={
                    readOnly ? undefined : (audience) => <CommentAudienceSwitch proposal={proposal} {...audience} />
                  }
                  readOnly={readOnly}
                />
              ))}
          </CommentsList>
        </>
      )}
    </CommentsContainer>
  );
}
